import { Box } from "@mui/material";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_EASE,
  SITE_MEDIA,
  SITE_QUERY,
} from "src/domains/shared/constants/styles/site";
import { SiteImage } from "../siteKit/media/SiteImage";
import { siteFileUrl, siteImageUrl } from "../siteKit/media/siteMedia";
import type { BridgePhase, BridgeSceneController } from "./bridgeScene";
import type { BridgeProfileData, PlotOptions } from "./profilePlot";
import type { SceneContext } from "./SceneSection";
import { easeOut } from "./sceneMath";
import { SCENE_STYLES } from "./sceneStyles";
import { truncateNumber } from "src/domains/shared/utils/truncateNumber";

/** 종단면·횡단면 단계는 3D 에 선을 긋는 장면부터 시작해, 다 그으면 단면 패널을 올린다 */
const BRIDGE_PHASES: readonly BridgePhase[] = ["lon", "crs", "dates", "export"];
export const BRIDGE_DURATIONS = [4600, 4600, 3600, 3000] as const;
const DRAW_MS = 1800;
/** 그래프를 왼쪽부터 그리는 시간·지난 촬영일 선이 늦게 따라오는 시간(ms) */
const REVEAL_MS = 1500;
const REVEAL_PAST_DELAY_MS = 500;
const REVEAL_PAST_ONLY_DELAY_MS = 150;
const REVEAL_PAST_MS = 1400;
const PULSE_PERIOD_MS = 700;
const SCENE_ROOT_MARGIN = "900px";
const TEXTURE_WIDTH = 1040;

const BRIDGE_STYLES = {
  dim: {
    position: "absolute",
    inset: 0,
    zIndex: 4,
    background: "rgba(15,23,42,.32)",
    opacity: 0,
    transition: "opacity .35s ease",
    pointerEvents: "none",
  },
  panel: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "52%",
    zIndex: 3,
    display: "flex",
    flexDirection: "column",
    background: "#fafafa",
    borderTop: "1px solid #E0E0E0",
    transform: "translateY(102%)",
    transition: `transform .7s ${SITE_EASE.out}`,
    "&[data-on]": { transform: "none" },
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      zIndex: 1,
      background: "rgba(15,23,42,.32)",
      opacity: 0,
      transition: "opacity .35s ease",
      pointerEvents: "none",
    },
    [SITE_MEDIA.tablet]: { height: "50%" },
  },
  head: {
    flex: "none",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    height: "44px",
    padding: "0 10px 0 12px",
    background: "#fff",
    borderBottom: "1px solid #E0E0E0",
    fontSize: "13px",
    color: "#212121",
    whiteSpace: "nowrap",
    overflow: "hidden",
    "& [data-kind]": {
      padding: "4px 10px",
      borderRadius: "999px",
      background: "rgba(0,0,0,.05)",
      fontWeight: 600,
    },
    "& [data-range]": { fontVariantNumeric: "tabular-nums", color: "#212121" },
    "& [data-spacer]": { flex: 1 },
    [SITE_MEDIA.phone]: { height: "38px", gap: "6px", fontSize: "12px" },
  },
  button: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    height: "30px",
    padding: "0 10px",
    border: "1px solid #E0E0E0",
    borderRadius: "6px",
    color: "#212121",
    background: "#fff",
    "& b": { fontWeight: 500 },
    [SITE_MEDIA.tablet]: { "& b": { display: "none" } },
    [SITE_MEDIA.phone]: { height: "26px", padding: "0 7px" },
  },
  body: { position: "relative", flex: 1, minHeight: 0 },
  plot: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    display: "block",
  },
  legend: {
    position: "absolute",
    right: "16px",
    top: "14px",
    display: "flex",
    gap: "12px",
    padding: "4px 8px",
    borderRadius: "6px",
    background: "rgba(255,255,255,.92)",
    border: "1px solid rgba(0,0,0,.08)",
    fontSize: "11px",
    color: "rgba(0,0,0,.72)",
    "& span": { display: "inline-flex", alignItems: "center", gap: "5px" },
    "& i": {
      display: "inline-block",
      width: "14px",
      height: "2px",
      borderRadius: "1px",
    },
    "& i[data-bim]": {
      height: "8px",
      width: "10px",
      background: "linear-gradient(90deg,#958198 50%,#8b9692 50%)",
    },
    [SITE_MEDIA.tablet]: {
      right: "8px",
      top: "8px",
      gap: "8px",
      fontSize: "10px",
    },
  },
  pop: {
    position: "absolute",
    right: "10px",
    top: "48px",
    width: "216px",
    padding: "4px 0 8px",
    borderRadius: "8px",
    background: "#fff",
    border: "1px solid #E0E0E0",
    boxShadow: "0 8px 24px rgba(20,30,45,.18)",
    opacity: 0,
    transform: "translateY(-6px)",
    transition: "opacity .3s,transform .3s",
    zIndex: 2,
    "&[data-on]": { opacity: 1, transform: "none" },
    "& [data-pop-head]": {
      padding: "2px 6px 6px 12px",
      borderBottom: "1px solid #E0E0E0",
      fontSize: "11px",
      fontWeight: 600,
      color: "#757575",
    },
    "& [data-pop-field]": {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      padding: "10px 12px",
      fontSize: "12px",
      color: "#616161",
      "& b": {
        flex: 1,
        padding: "5px 8px",
        border: "1px solid #E0E0E0",
        borderRadius: "4px",
        fontWeight: 500,
        color: "#212121",
        textAlign: "right",
      },
      "& em": { fontStyle: "normal" },
    },
    "& [data-pop-formats]": {
      display: "flex",
      gap: "6px",
      padding: "8px 12px 0",
      borderTop: "1px solid #E0E0E0",
      "& i": {
        flex: 1,
        fontStyle: "normal",
        textAlign: "center",
        padding: "5px 0",
        borderRadius: "6px",
        border: "1px solid #E0E0E0",
        fontSize: "12px",
        fontWeight: 600,
        color: "#212121",
      },
      "& i:first-of-type": {
        borderColor: "#1D74FF",
        color: "#1D74FF",
        background: "rgba(29,116,255,.08)",
      },
    },
    [SITE_MEDIA.phone]: { top: "42px", width: "190px" },
  },
} as const;

const CAMERA_ICON = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 7h1a2 2 0 0 0 2-2a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1a2 2 0 0 0 2 2h1a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2" />
    <circle cx="12" cy="13" r="3" />
  </svg>
);

const EXPORT_ICON = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
    <path d="M11.5 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v5m-5 6h7m-3-3l3 3l-3 3" />
  </svg>
);

/**
 * 단면 장면 칸 내용: 대체 그림·3D 표시·단면 패널(그래프·범례·내보내기 팝업). 종단면·횡단면 단계는 선을 긋는 동안 패널을
 * 내렸다가 다 그으면 올리고 그래프를 왼쪽부터 그린다. 시계열 단계는 지난 촬영일 선을 깜빡여 눈에 띄게 한다.
 */
export function BridgeSceneContent({ context }: { context: SceneContext }) {
  const { t } = useTranslation("landing");
  const {
    rootRef,
    step,
    previousStep,
    snap,
    active,
    activeRef,
    reducedMotion,
  } = context;
  const fallbackRef = useRef<HTMLImageElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const kindRef = useRef<HTMLSpanElement | null>(null);
  const rangeRef = useRef<HTMLSpanElement | null>(null);
  const plotRef = useRef<HTMLCanvasElement | null>(null);
  const pastLegendRef = useRef<HTMLSpanElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const dataRef = useRef<BridgeProfileData | null>(null);
  const plotModuleRef = useRef<typeof import("./profilePlot") | null>(null);
  const sceneRef = useRef<BridgeSceneController | null>(null);
  const drawingRef = useRef(false);
  const revealRef = useRef({ progress: 1, past: 1 });
  const frameRef = useRef(0);
  const phaseTimerRef = useRef(0);
  const phase = BRIDGE_PHASES[step];
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const labels = {
    distance: t("site.scenes.bridge.distance"),
    height: t("site.scenes.bridge.height"),
  };
  const labelsRef = useRef(labels);
  labelsRef.current = labels;

  const plot = (progress: number, pastProgress = progress, blink = -1) => {
    const data = dataRef.current;
    const canvas = plotRef.current;
    const plotModule = plotModuleRef.current;
    if (!data || !canvas || !plotModule) return;
    const current = phaseRef.current;
    const options: PlotOptions = {
      kind: current === "crs" ? "crs" : "lon",
      withPast: current === "dates" || current === "export",
      progress,
      pastProgress,
      blink,
      narrow: window.matchMedia(SITE_QUERY.tablet).matches,
    };
    plotModule.drawProfile(canvas, data, options, labelsRef.current);
  };

  const setPanel = () => {
    const root = rootRef.current;
    const current = phaseRef.current;
    const show = !drawingRef.current;
    panelRef.current?.toggleAttribute("data-on", show);
    root?.toggleAttribute("data-panel", show);
    const kind = current === "crs" ? "crs" : "lon";
    if (kindRef.current)
      kindRef.current.textContent = t(`site.scenes.bridge.${kind}`);
    const data = dataRef.current;
    if (data && rangeRef.current)
      rangeRef.current.textContent = `0 ~ ${truncateNumber(data[kind].L, 3)}`;
    pastLegendRef.current?.toggleAttribute(
      "hidden",
      !(current === "dates" || current === "export")
    );
    popRef.current?.toggleAttribute("data-on", current === "export");
    root?.toggleAttribute("data-export", current === "export");
  };

  // 그래프를 왼쪽부터 그린다 — 지난 촬영일 선은 조금 늦게 따라 그린다. 시계열 단계는 다 그린 뒤 깜빡인다
  const pulse = () => {
    cancelAnimationFrame(frameRef.current);
    if (reducedMotion || !activeRef.current) return;
    const start = performance.now();
    const frame = (now: number) => {
      if (phaseRef.current !== "dates" || !activeRef.current) return;
      plot(
        1,
        1,
        0.5 + 0.5 * Math.cos(((now - start) / PULSE_PERIOD_MS) * Math.PI * 2)
      );
      frameRef.current = requestAnimationFrame(frame);
    };
    frameRef.current = requestAnimationFrame(frame);
  };
  const reveal = (pastOnly = false) => {
    cancelAnimationFrame(frameRef.current);
    if (reducedMotion || !activeRef.current) {
      revealRef.current = { progress: 1, past: 1 };
      plot(1);
      return;
    }
    const start = performance.now();
    const frame = (now: number) => {
      const elapsed = now - start;
      const progress = pastOnly ? 1 : Math.min(1, elapsed / REVEAL_MS);
      const past = Math.min(
        1,
        Math.max(
          0,
          (elapsed -
            (pastOnly ? REVEAL_PAST_ONLY_DELAY_MS : REVEAL_PAST_DELAY_MS)) /
            REVEAL_PAST_MS
        )
      );
      revealRef.current = { progress, past };
      plot(easeOut(progress), easeOut(past));
      if (progress < 1 || past < 1)
        frameRef.current = requestAnimationFrame(frame);
      else if (phaseRef.current === "dates") pulse();
    };
    frameRef.current = requestAnimationFrame(frame);
  };

  // 화면 가까이 오면 그래프 값·장면 엔진을 받는다(그래프는 3D 없이도 그린다)
  useEffect(() => {
    const root = rootRef.current;
    const fallback = fallbackRef.current;
    if (!root || !fallback) return undefined;
    let disposed = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        void (async () => {
          const [plotModule, profile] = await Promise.all([
            import("./profilePlot"),
            fetch(siteFileUrl("data/br/br_profile.json")).then(
              (response) => response.json() as Promise<BridgeProfileData>
            ),
          ]);
          if (disposed) return;
          plotModuleRef.current = plotModule;
          dataRef.current = profile;
          setPanel();
          plot(revealRef.current.progress);
          const [assetsModule, bridgeModule] = await Promise.all([
            import("./sceneAssets"),
            import("./bridgeScene"),
          ]);
          if (disposed || !assetsModule.canUseWebGL()) return;
          const [ortho, height, bim] = await Promise.all([
            assetsModule.loadTextureFactory(
              siteFileUrl("data/br/br_ortho.ktx2"),
              siteImageUrl("br_ortho", TEXTURE_WIDTH)
            ),
            assetsModule.loadImage(siteFileUrl("data/br/br_h.png")),
            fetch(siteFileUrl("data/br/br_bim.bin")).then((response) =>
              response.arrayBuffer()
            ),
          ]);
          if (disposed) return;
          const scene = await bridgeModule.createBridgeScene(
            root,
            fallback,
            { ortho, height, bim, profile },
            () => activeRef.current,
            reducedMotion
          );
          if (disposed) {
            scene.dispose();
            return;
          }
          sceneRef.current = scene;
          scene.update(phaseRef.current, drawingRef.current, true);
        })().catch(() => {
          sceneRef.current = null;
        });
      },
      { rootMargin: SCENE_ROOT_MARGIN }
    );
    observer.observe(root);
    const canvas = plotRef.current;
    const resizeObserver = new ResizeObserver(() =>
      plot(revealRef.current.progress)
    );
    if (canvas) resizeObserver.observe(canvas);
    return () => {
      disposed = true;
      observer.disconnect();
      resizeObserver.disconnect();
      cancelAnimationFrame(frameRef.current);
      window.clearTimeout(phaseTimerRef.current);
      sceneRef.current?.dispose();
      sceneRef.current = null;
    };
    // 장면은 한 번만 만든다 — 단계 변화는 아래 효과가 넘긴다
  }, []);

  // 단계 전환: 선 긋기 → 패널 올림 → 그래프 그리기
  useEffect(() => {
    const previousPhase = BRIDGE_PHASES[previousStep];
    window.clearTimeout(phaseTimerRef.current);
    cancelAnimationFrame(frameRef.current);
    const drawStep = phase === "lon" || phase === "crs";
    drawingRef.current = drawStep && !reducedMotion && activeRef.current;
    setPanel();
    if (drawingRef.current) {
      revealRef.current = { progress: 0, past: 0 };
      plot(0);
      phaseTimerRef.current = window.setTimeout(() => {
        drawingRef.current = false;
        setPanel();
        reveal();
        sceneRef.current?.update(phaseRef.current, false);
      }, DRAW_MS);
    } else if (drawStep) {
      revealRef.current = { progress: 1, past: 1 };
      plot(1);
    } else if (phase === "dates") {
      reveal(previousPhase === "lon" && !snap);
    } else {
      revealRef.current = { progress: 1, past: 1 };
      plot(1);
    }
    sceneRef.current?.update(phase, drawingRef.current, snap);
    // 단계가 바뀔 때만 — 도우미 함수는 ref 로 최신 값을 읽는다
  }, [step, previousStep, snap]);

  useEffect(() => {
    if (active) sceneRef.current?.wake();
  }, [active]);

  return (
    <>
      <SiteImage
        ref={fallbackRef}
        name="br_ortho"
        sizes="(max-width: 900px) 50vw, 440px"
        alt=""
        placeholder="none"
        sx={SCENE_STYLES.fallback}
      />
      <Box aria-hidden="true" sx={SCENE_STYLES.hud}>
        <Box component="span" sx={SCENE_STYLES.mode}>
          <b data-on="">3D</b>
        </Box>
      </Box>
      <Box data-dim="" aria-hidden="true" sx={BRIDGE_STYLES.dim} />
      <Box
        ref={panelRef}
        data-panel-box=""
        aria-hidden="true"
        sx={BRIDGE_STYLES.panel}
      >
        <Box sx={BRIDGE_STYLES.head}>
          <span data-kind="" ref={kindRef}>
            {t("site.scenes.bridge.lon")}
          </span>
          <span data-range="" ref={rangeRef}>
            0 ~ 236.000
          </span>
          <span data-spacer="" />
          <Box component="span" sx={BRIDGE_STYLES.button}>
            {CAMERA_ICON}
            <b>{t("site.scenes.bridge.capture")}</b>
          </Box>
          <Box component="span" sx={BRIDGE_STYLES.button}>
            {EXPORT_ICON}
            <b>{t("site.scenes.bridge.extract")}</b>
          </Box>
        </Box>
        <Box sx={BRIDGE_STYLES.body}>
          <Box component="canvas" ref={plotRef} sx={BRIDGE_STYLES.plot} />
          <Box sx={BRIDGE_STYLES.legend}>
            <span>
              <i style={{ background: "#008FFB" }} />
              {t("site.scenes.bridge.dateNow")}
            </span>
            <span ref={pastLegendRef} hidden>
              <i style={{ background: "#7C4DFF" }} />
              {t("site.scenes.bridge.datePast")}
            </span>
            <span>
              <i data-bim="" />
              {t("site.scenes.bridge.bim")}
            </span>
          </Box>
        </Box>
        <Box ref={popRef} data-pop="" sx={BRIDGE_STYLES.pop}>
          <div data-pop-head="">{t("site.scenes.bridge.popTitle")}</div>
          <div data-pop-field="">
            <span>{t("site.scenes.bridge.popSpacing")}</span>
            <b>1.0</b>
            <em>{t("site.scenes.bridge.popUnit")}</em>
          </div>
          <div data-pop-formats="">
            <i>.txt</i>
            <i>.csv</i>
            <i>.dxf</i>
          </div>
        </Box>
      </Box>
    </>
  );
}
